import React from 'react';

const formatFrequency = (frequencyType, frequencyValue) => {
  if (!frequencyType) return '';
  const value = frequencyValue || 1;
  const t = frequencyType.toLowerCase();
  if (t.startsWith('week')) return value === 1 ? 'Weekly' : `Every ${value} weeks`;
  if (t.startsWith('month')) return value === 1 ? 'Monthly' : `Every ${value} months`;
  if (t.startsWith('day')) return value === 1 ? 'Daily' : `Every ${value} days`;
  return `Every ${value} ${frequencyType}`;
};

const DeleteHouseholdModal = ({
  isOpen,
  onClose,
  onConfirm,
  householdName,
  schedules,
  isLoading,
  loadingText,
}) => {
  if (!isOpen) return null;

  const scheduleCount = schedules?.length || 0;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 h-full w-full z-50 flex justify-center items-center p-4">
      <div className="relative mx-auto border w-full max-w-3xl shadow-lg rounded-md bg-white flex flex-col max-h-[90vh]">
        <h3 className="text-lg leading-6 font-medium text-gray-900 flex-shrink-0 px-5 pt-5 pb-2 text-center">
          Confirm Household Deletion
        </h3>
        <div className="px-7 py-3 overflow-y-auto flex-1 min-h-0">
          <p className="text-sm text-gray-500 text-center mb-4">
            Are you sure you want to delete the household <strong>{householdName}</strong>?
          </p>

          {scheduleCount > 0 ? (
            <>
              <div className="bg-yellow-50 border border-yellow-200 rounded-md px-4 py-3 mb-4">
                <p className="text-sm text-yellow-800">
                  This household has <strong>{scheduleCount} active job schedule{scheduleCount !== 1 ? 's' : ''}</strong> that will be <strong>inactivated</strong>.
                  Active jobs on these schedules will no longer appear in the franchisee app.
                </p>
              </div>

              <div className="border border-gray-200 rounded-md overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Service</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Frequency</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Franchisee</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Start Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {schedules.map((s) => {
                      const serviceName = s.services?.[0]?.name || s.serviceType || 'N/A';
                      const freq = formatFrequency(s.frequencyType, s.frequencyValue);
                      const fseName = [s.franchiseeInfo?.firstName, s.franchiseeInfo?.lastName].filter(Boolean).join(' ') || s.fseId || 'N/A';
                      let startDate = 'N/A';
                      if (s.startDate) {
                        const d = s.startDate?.toDate ? s.startDate.toDate() : (s.startDate._seconds ? new Date(s.startDate._seconds * 1000) : new Date(s.startDate));
                        if (!isNaN(d.getTime())) startDate = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
                      }
                      return (
                        <tr key={s.id}>
                          <td className="px-3 py-2 text-gray-900">{serviceName}</td>
                          <td className="px-3 py-2 text-gray-900">{freq || 'N/A'}</td>
                          <td className="px-3 py-2 text-gray-900">{fseName}</td>
                          <td className="px-3 py-2 text-gray-900">{startDate}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500 text-center">
              No active job schedules found for this household.
            </p>
          )}

          <p className="text-xs text-gray-400 text-center mt-4">
            Completed and invoiced jobs will not be affected.
          </p>
        </div>
        <div className="items-center px-5 py-3 space-x-3 flex justify-end flex-shrink-0 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {loadingText || 'Processing...'}
              </>
            ) : 'Delete Household'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteHouseholdModal;
